import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

/**
 * Aranabilir tek-seçim dropdown (combobox).
 *
 * Native <select> + ayrı "yazıp daralt" kutusunun yarattığı kafa karışıklığını
 * giderir: tek bir kutuya tıklayıp içine yazarak seçenek aranır ve seçilir.
 *
 * Props:
 *  - value: seçili seçeneğin id'si veya boş-değer (allValue, varsayılan "all")
 *  - onChange(id): seçim değişince çağrılır (boş-değer => tümü/atanmamış)
 *  - options: [{ id, name, disabled?, title?, icon?, group? }]
 *      • disabled: seçenek tıklanamaz/gri (örn. müsait olmayan personel)
 *      • title: üzerine gelince çıkan açıklama (tooltip)
 *      • icon: ad'ın solunda gösterilen ikon (React node)
 *      • group: aynı grup başlığı altında toplanır (gruplu liste + ayraç)
 *  - allLabel: boş/"tümü" seçeneğinin etiketi (örn. "Tüm Parklar", "Atanmamış")
 *  - allValue: boş seçeneğin değeri (filtrelerde "all", formlarda "none"/"" olabilir)
 *  - showAllOption: boş/"tümü" seçeneği listede gösterilsin mi (zorunlu alanlarda false)
 *  - placeholder: seçim yokken ve boş seçenek gizliyken gösterilen metin
 *  - disabled: salt-okunur/kilitli durum
 *  - searchPlaceholder: arama kutusu placeholder'ı (örn. "Park ara...")
 *  - emptyText: eşleşme yokken gösterilen metin
 *  - className: tetikleyici buton ek sınıfları
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  allLabel = "Tümü",
  allValue = "all",
  showAllOption = true,
  placeholder,
  disabled = false,
  searchPlaceholder = "Ara...",
  emptyText = "Sonuç bulunamadı.",
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selected =
    value != null && value !== allValue ? options.find((o) => o.id === value) : null;
  const label = selected
    ? selected.name
    : showAllOption
    ? allLabel
    : placeholder ?? allLabel;

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
  };

  // Gruplama: herhangi bir seçenekte `group` varsa gruplu (başlık + ayraç) render.
  const hasGroups = options.some((o) => o.group);
  const orderedGroups = [];
  if (hasGroups) {
    options.forEach((o) => {
      const g = o.group || "";
      if (!orderedGroups.includes(g)) orderedGroups.push(g);
    });
  }

  const renderItem = (o) => (
    <CommandItem
      key={o.id}
      value={`${o.name} ${o.id}`}
      disabled={!!o.disabled}
      title={o.title}
      onSelect={() => {
        if (!o.disabled) handleSelect(o.id);
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          value === o.id ? "opacity-100" : "opacity-0"
        )}
      />
      {o.icon ? (
        <span className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center">
          {o.icon}
        </span>
      ) : null}
      <span className="truncate">{o.name}</span>
    </CommandItem>
  );

  const allItem = showAllOption ? (
    <CommandItem value={allLabel} onSelect={() => handleSelect(allValue)}>
      <Check
        className={cn("mr-2 h-4 w-4 shrink-0", !selected ? "opacity-100" : "opacity-0")}
      />
      {allLabel}
    </CommandItem>
  ) : null;

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span
            className={cn(
              "flex items-center gap-1.5 truncate",
              selected ? "" : "text-muted-foreground"
            )}
          >
            {selected?.icon ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {selected.icon}
              </span>
            ) : null}
            {label}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[11rem] max-w-[var(--radix-popover-content-available-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {hasGroups ? (
              <>
                {showAllOption && <CommandGroup>{allItem}</CommandGroup>}
                {orderedGroups.map((g, i) => (
                  <React.Fragment key={g || `__g${i}`}>
                    {(i > 0 || showAllOption) && <CommandSeparator />}
                    <CommandGroup heading={g || undefined}>
                      {options
                        .filter((o) => (o.group || "") === g)
                        .map(renderItem)}
                    </CommandGroup>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <CommandGroup>
                {allItem}
                {options.map(renderItem)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
